'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { apiBase, fetchWithTimeout } from '@/lib/api';

type Props = {
  eventId: string;
  speakerMode: 'managed' | 'recruitment';
};

type MyMemberResponse = {
  exists: boolean;
  member: {
    id: string;
    displayName: string;
    membershipType: string;
    membershipNumber: string | null;
  } | null;
};

/**
 * EventSpeakerRecruitClient
 *
 * 目的:
 * - 「講師募集」モードのイベントで、応募フォームを提供する
 *
 * 仕様:
 * - 応募は有料会員（承認済み）限定（バックエンドでも検証）
 *
 * 想定される例外:
 * - 未ログイン: ログイン導線を表示
 * - 会員情報が取得できない: エラーメッセージを表示
 */
export default function EventSpeakerRecruitClient(props: Props) {
  const { token, user, loading } = useAuth();
  const [memberLoading, setMemberLoading] = useState(false);
  const [member, setMember] = useState<MyMemberResponse['member']>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [career, setCareer] = useState('');
  const [seminarTitle, setSeminarTitle] = useState('');
  const [seminarOutline, setSeminarOutline] = useState('');
  const [takeaway, setTakeaway] = useState('');
  const [hasReadGuidelines, setHasReadGuidelines] = useState(false);

  const canShow = props.speakerMode === 'recruitment';

  useEffect(() => {
    if (!token || !canShow) return;
    setMemberLoading(true);
    fetch(`${apiBase()}/api/auth/my/member`, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      cache: 'no-store',
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('会員情報の取得に失敗しました。');
        return (await res.json()) as MyMemberResponse;
      })
      .then((data) => {
        setMember(data.member ?? null);
      })
      .catch((e) => {
        console.error('[講師応募: 会員情報取得エラー]', e);
        setError(e?.message ?? '会員情報の取得に失敗しました。');
      })
      .finally(() => setMemberLoading(false));
  }, [token, canShow]);

  const isPaidMember = useMemo(() => {
    const t = member?.membershipType;
    return t === 'paid_female' || t === 'paid_male';
  }, [member?.membershipType]);

  const submit = async () => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetchWithTimeout(
        `${apiBase()}/api/events/${props.eventId}/speaker-applications`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            career: career.trim() ? career : null,
            seminarTitle: seminarTitle.trim(),
            seminarOutline: seminarOutline.trim(),
            takeaway: takeaway.trim(),
          }),
        },
        15000
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message ?? '応募の送信に失敗しました。');
      }
      setDone(true);
    } catch (e: any) {
      console.error('[講師応募エラー]', e);
      setError(e?.message ?? '応募の送信に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  if (!canShow) return null;
  if (loading) return null;

  if (!user) {
    return (
      <div className='mt-6 border border-line bg-white p-5 shadow-sm'>
        <p className='text-sm font-semibold text-gray-800'>
          講師応募は会員ログインが必要です
        </p>
        <p className='mt-2 text-sm text-gray-600'>
          応募にはログインが必要です。
        </p>
        <div className='mt-3 flex flex-col gap-2 md:flex-row'>
          <Link
            href='/login'
            className='inline-flex flex-1 items-center justify-center bg-primary px-4 py-2 text-sm font-semibold text-white'
          >
            ログインする
          </Link>
          <Link
            href='/register'
            className='inline-flex flex-1 items-center justify-center border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-800'
          >
            会員登録する
          </Link>
        </div>
      </div>
    );
  }

  if (memberLoading) return null;

  if (member && !isPaidMember) {
    return (
      <div className='mt-6 border border-amber-200 bg-amber-50 p-5 text-amber-900'>
        <p className='font-semibold'>講師応募は有料会員限定です。</p>
        <p className='mt-2 text-sm text-amber-900/80'>
          有料会員への切り替えをご希望の場合は、結会本部へお問い合わせください。
        </p>
      </div>
    );
  }

  if (done) {
    return (
      <div className='mt-6 rounded border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800'>
        応募を受け付けました。結会本部よりご連絡します。
      </div>
    );
  }

  return (
    <div className='mt-8 border border-line bg-white p-6 shadow-sm'>
      <h3 className='font-serif text-xl text-gray-800'>プチセミナー講師 募集</h3>
      <p className='mt-3 whitespace-pre-line text-[15px] leading-7 text-gray-700/80'>
        結会が毎月開催している「お茶会」の中で行う【30分間のプチセミナー講師】についてのご案内です。
        {'\n'}
        「結会会員が3,000円でこの話を聞けるなんて嬉しい、お得！」そう感じてもらえる、実りのある時間を目的にしています。
        {'\n'}
        講師にとっても、集客の負担なく経験や想いを伝えられ、ご自身のビジネスにつながる可能性のある良いチャンスです。
      </p>

      <div className='mt-5 rounded border border-line bg-gray-50 p-4 text-sm text-gray-700'>
        <p className='font-semibold'>応募時にお知らせいただきたいこと</p>
        <ol className='mt-2 list-decimal space-y-1 pl-5'>
          <li>お名前／会員ナンバー</li>
          <li>簡単なご経歴・活動内容</li>
          <li>プチセミナータイトル</li>
          <li>30分でお話しする内容の概要</li>
          <li>参加者にどんな時間を持ち帰ってほしいか</li>
        </ol>
        <p className='mt-3 text-xs text-gray-600'>
          募集要項の全文は{' '}
          <Link
            href='/speaker-recruitment'
            target='_blank'
            rel='noopener noreferrer'
            className='text-primary underline'
          >
            募集要項（全文）
          </Link>{' '}
          をご確認ください。
        </p>
      </div>

      {error && (
        <div className='mt-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
          {error}
        </div>
      )}

      <div className='mt-6 grid grid-cols-1 gap-4'>
        <div className='rounded border border-line bg-white p-4'>
          <p className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
            お名前 / 会員ナンバー
          </p>
          <p className='mt-2 text-sm text-gray-800'>
            {member?.displayName ?? user.displayName}
            {member?.membershipNumber ? `（${member.membershipNumber}）` : ''}
          </p>
        </div>

        <div>
          <label className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
            簡単なご経歴・活動内容
          </label>
          <textarea
            rows={3}
            value={career}
            onChange={(e) => setCareer(e.target.value)}
            className='mt-2 w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-primary focus:outline-none'
            placeholder='例: ○○のコーチとして…'
          />
        </div>

        <div>
          <label className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
            プチセミナータイトル <span className='text-red-500'>*</span>
          </label>
          <input
            value={seminarTitle}
            onChange={(e) => setSeminarTitle(e.target.value)}
            className='mt-2 w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-primary focus:outline-none'
            placeholder='例: ○○を続けるコツ'
          />
        </div>

        <div>
          <label className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
            30分でお話しする内容の概要 <span className='text-red-500'>*</span>
          </label>
          <textarea
            rows={5}
            value={seminarOutline}
            onChange={(e) => setSeminarOutline(e.target.value)}
            className='mt-2 w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-primary focus:outline-none'
            placeholder='例: 前半10分で…'
          />
        </div>

        <div>
          <label className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
            参加者に持ち帰ってほしいこと <span className='text-red-500'>*</span>
          </label>
          <textarea
            rows={4}
            value={takeaway}
            onChange={(e) => setTakeaway(e.target.value)}
            className='mt-2 w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-primary focus:outline-none'
            placeholder='例: 明日から1つでも…'
          />
        </div>
      </div>

      <label className='mt-6 flex items-start gap-3 text-sm text-gray-700'>
        <input
          type='checkbox'
          checked={hasReadGuidelines}
          onChange={(e) => setHasReadGuidelines(e.target.checked)}
          className='mt-1 h-4 w-4 accent-primary'
        />
        <span>
          <Link
            href='/speaker-recruitment'
            target='_blank'
            rel='noopener noreferrer'
            className='text-primary underline'
          >
            募集要項（全文）
          </Link>
          を読みました
        </span>
      </label>

      <button
        type='button'
        onClick={submit}
        disabled={
          submitting ||
          !seminarTitle.trim() ||
          !seminarOutline.trim() ||
          !takeaway.trim() ||
          !hasReadGuidelines
        }
        className='mt-6 inline-flex w-full items-center justify-center bg-primary px-5 py-3 text-sm font-semibold text-white disabled:opacity-60 md:w-auto'
      >
        {submitting ? '送信中...' : 'この内容で応募する'}
      </button>
    </div>
  );
}


